import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRestApiProvider, WhatsappMessageModel } from "@jugl-web/rest-api";
import {
  DataLoadingWrapper,
  LoadingAnimation,
  MultiSectionLayout,
  PageLoaderFull,
} from "@jugl-web/ui-components";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { VList, VListHandle } from "virtua";
import {
  getUniqueId,
  isoToLocalDate,
  useToast,
  useTranslations,
} from "@jugl-web/utils";
import isToday from "date-fns/isToday";
import isThisYear from "date-fns/isThisYear";
import format from "date-fns/format";
import { useLanguage } from "@jugl-web/utils/i18n/EnhancedIntlProvider";
import useDebounce from "@web-src/hooks/useDebounce";
import { ChatDateHeader } from "@web-src/modules/chats/pages/ChatsPage/components/ChatWindow/components/ChatMessagesWindow/components/ChatDateHeader";
import { ChatMessageReplyInfo } from "@web-src/modules/chats/components/CommonMessageBubble/types/ChatMessageReplyInfo";
import { WhatsappMessageItem } from "../WhatsappMessageItem";
import { ReactComponent as ChevronsDownIcon } from "./assets/chevrons-down.svg";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";

export interface WhatsappChatMessageWindowHandle {
  scrollToBottom: () => void;
  addMessage: (
    message: WhatsappMessageModel,
    forceScrollToBottom?: boolean
  ) => void;
}

type WhatsappChatMessageItem =
  | { type: "message"; value: WhatsappMessageModel }
  | { type: "date"; value: string };

export const WhatsappChatMessageWindow = forwardRef<
  WhatsappChatMessageWindowHandle,
  { conversationId: string }
>(({ conversationId }, ref) => {
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const { t } = useTranslations();
  const { toast } = useToast();
  const { dateLocale } = useLanguage();
  const requestId = useRef<string>(undefined);
  const vListRef = useRef<VListHandle>(null);
  const [atBottom, setAtBottom] = useState(true);
  const debouncedAtBottom = useDebounce(atBottom, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadedMessages, setLoadedMessages] = useState<WhatsappMessageModel[]>(
    []
  );
  const [replyTargetId, setReplyTargetId] = useState<string>();
  const { incomingWhatsappMessages$ } = useClientSupportPageContext();

  const [isError, setIsError] = useState(false);
  const [isAddingMessages, setIsAddingMessages] = useState(false);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const PAGE_SIZE = 50;
  const [isInitialScrollInitialized, setIsInitialScrollInitialized] =
    useState(false);
  const isInitialScrollInitializationInProgress = useRef(false);

  const [fetchMessages, { isFetching }] =
    integrationsApi.useLazyGetWhatsappMessagesQuery();
  const [fetchSingleMessage] =
    integrationsApi.useLazyGetWhatsappMessageByIdQuery();

  const whatsappMessageItems = useMemo(() => {
    if (!loadedMessages.length) return [];

    const result: WhatsappChatMessageItem[] = [];
    const addedDates: string[] = [];

    loadedMessages.forEach((message) => {
      const date = isoToLocalDate(message.payload.timestamp);
      const dateStrAlt = format(date, "yyyy-MM-dd");

      if (!addedDates.includes(dateStrAlt)) {
        addedDates.push(dateStrAlt);
        const dateStr = isToday(date)
          ? t({
              id: "common.today",
              defaultMessage: "Today",
            })
          : format(date, isThisYear(date) ? "d MMMM" : "d MMMM yyyy", {
              locale: dateLocale,
            });

        result.push({ type: "date", value: dateStr });
      }

      result.push({ type: "message", value: message });
    });

    return result;
  }, [loadedMessages, t, dateLocale]);

  const loadMessages = useCallback(
    async (page: number) => {
      try {
        setIsError(false);
        setIsAddingMessages(true);

        const newRequestId = getUniqueId();
        requestId.current = newRequestId;

        const result = await fetchMessages({
          entityId,
          params: {
            conv_id: conversationId,
            from: "2040-01-01T00:00:00",
            page_size: PAGE_SIZE,
            page,
          },
        }).unwrap();

        if (requestId.current !== newRequestId) return;
        if (result) {
          setLoadedMessages((prev) => [...[...result.data].reverse(), ...prev]);
          setTotalPages(result.total_pages);
        }
      } catch (error) {
        setIsError(true);
      } finally {
        setTimeout(() => {
          setIsAddingMessages(false);
        }, 0);
      }
    },
    [entityId, conversationId, fetchMessages, requestId]
  );

  // Reset state when conversation changes
  useEffect(() => {
    setLoadedMessages([]);
    setCurrentPage(1);
    setIsError(false);
    setIsInitialScrollInitialized(false);
    isInitialScrollInitializationInProgress.current = false;
    setTotalPages(null);
    loadMessages(1);
  }, [conversationId, loadMessages]);

  // Initial scroll initialization
  useEffect(() => {
    if (
      isFetching ||
      !whatsappMessageItems.length ||
      isInitialScrollInitialized ||
      isInitialScrollInitializationInProgress.current
    ) {
      return;
    }

    isInitialScrollInitializationInProgress.current = true;
    vListRef.current?.scrollToIndex(whatsappMessageItems.length - 1, {
      align: "end",
    });

    setTimeout(() => {
      setIsInitialScrollInitialized(true);
      isInitialScrollInitializationInProgress.current = false;
    }, 500);
  }, [whatsappMessageItems, isFetching, isInitialScrollInitialized]);

  const loadMore = useCallback(() => {
    if (isFetching || !isInitialScrollInitialized) return;
    setCurrentPage((prev) => prev + 1);
    loadMessages(currentPage + 1);
  }, [isFetching, currentPage, loadMessages, isInitialScrollInitialized]);

  const handleVListOnScroll = useCallback(
    (offset: number) => {
      if (!vListRef.current || isFetching || !isInitialScrollInitialized) {
        return;
      }

      const bottomOffset =
        offset - vListRef.current.scrollSize + vListRef.current.viewportSize;

      setAtBottom(bottomOffset > -5);

      // Load more messages when scrolling up
      if (offset < 150 && (totalPages === null || currentPage < totalPages)) {
        loadMore();
      }
    },
    [isFetching, loadMore, isInitialScrollInitialized, totalPages, currentPage]
  );

  const handleScrollToBottom = useCallback(() => {
    vListRef.current?.scrollToIndex(whatsappMessageItems.length, {
      align: "end",
    });
  }, [whatsappMessageItems]);

  useEffect(() => {
    const subscription = incomingWhatsappMessages$.subscribe((message) => {
      if (message.conv_id !== conversationId) return;
      setLoadedMessages((prev) => {
        const existingMessageIndex = prev.findIndex(
          (m) => m.msg_id === message.msg_id
        );
        if (existingMessageIndex >= 0) {
          // Replace existing message
          const newMessages = [...prev];
          newMessages[existingMessageIndex] = {
            ...newMessages[existingMessageIndex],
            ...message,
          };
          return newMessages;
        }
        // Load source message asyncronously
        const contextMessageId = message.payload.context?.id;
        if (contextMessageId) {
          fetchSingleMessage({
            entityId,
            params: {
              msg_id: contextMessageId,
            },
          }).then((res) => {
            const reply = res?.data?.data?.length ? res.data.data[0] : null;
            if (reply) {
              setLoadedMessages((prevInside) => {
                const newMessagesInside = [...prevInside];
                const newMessage = newMessagesInside.find(
                  (item) => item.msg_id === message.msg_id
                );
                if (newMessage) {
                  newMessage.reply = reply;
                }
                return newMessagesInside;
              });
            }
          });
        }
        // Add new message
        return [...prev, message];
      });
    });
    if (atBottom) {
      setTimeout(() => {
        handleScrollToBottom();
      }, 100);
    }
    return () => {
      subscription.unsubscribe();
    };
  }, [
    incomingWhatsappMessages$,
    handleScrollToBottom,
    conversationId,
    atBottom,
    fetchSingleMessage,
    entityId,
  ]);

  const conversationInitialized = useRef<string>(undefined);
  useEffect(() => {
    if (
      !whatsappMessageItems.length ||
      conversationInitialized.current === conversationId
    ) {
      return () => {};
    }
    const timeoutId = setTimeout(() => {
      if (whatsappMessageItems.length) {
        conversationInitialized.current = conversationId;
        vListRef.current?.scrollToIndex(whatsappMessageItems.length - 1, {
          align: "end",
        });
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [conversationId, whatsappMessageItems]);

  useImperativeHandle(ref, () => ({
    scrollToBottom: handleScrollToBottom,
    addMessage: (
      message: WhatsappMessageModel,
      forceScrollToBottom = false
    ) => {
      setLoadedMessages((prev) => [...prev, message]);
      if (forceScrollToBottom) {
        setTimeout(() => {
          handleScrollToBottom();
        }, 0);
      }
    },
  }));

  useEffect(() => {
    if (!replyTargetId || isFetching || isAddingMessages) return;

    const index = whatsappMessageItems.findIndex(
      (m) => m.type === "message" && m.value.msg_id === replyTargetId
    );

    if (index >= 0) {
      setTimeout(
        () =>
          vListRef.current?.scrollToIndex(index, {
            align: "center",
          }),
        150
      );
      setReplyTargetId(undefined);
    } else if (totalPages === null || currentPage < totalPages) {
      loadMore();
    } else {
      toast(
        t({
          id: "chats-page.message-not-found",
          defaultMessage: "Message not found",
        }),
        { variant: "error" }
      );
      setReplyTargetId(undefined);
    }
  }, [
    currentPage,
    isAddingMessages,
    isFetching,
    loadMore,
    toast,
    replyTargetId,
    t,
    totalPages,
    whatsappMessageItems,
  ]);

  const handleReplyPreviewClick = useCallback(
    (replyInfo: ChatMessageReplyInfo) => setReplyTargetId(replyInfo.replyMsgId),
    []
  );

  if ((isFetching && currentPage === 1) || isError) {
    return (
      <DataLoadingWrapper
        isLoading={isFetching && currentPage === 1}
        isError={isError}
        onRetry={() => loadMessages(currentPage)}
      />
    );
  }

  if (!whatsappMessageItems.length && !isFetching) {
    return (
      <div className="mt-6 flex w-full items-center justify-center text-center font-secondary text-sm leading-[21px] font-medium text-primary-800">
        {t({
          id: "client-support-page.no-chat-history-yet",
          defaultMessage: "No Chat History yet 👋",
        })}
      </div>
    );
  }

  return (
    <>
      <PageLoaderFull isTransparent isActive={!!replyTargetId} />
      <MultiSectionLayout
        className="bg-gray-100"
        classNames={{ content: "relative" }}
      >
        <VList
          overscan={10}
          className="flex-1 pb-6"
          reverse
          shift={isAddingMessages}
          ref={vListRef}
          onScroll={handleVListOnScroll}
        >
          {isFetching && currentPage > 1 && (
            <div className="flex justify-center py-2">
              <LoadingAnimation size="md" />
            </div>
          )}
          {whatsappMessageItems.map((item, index) => (
            <div
              key={item.type === "message" ? item.value.id : `date-${+index}`}
              className="mx-auto w-full max-w-[1000px]"
            >
              {item.type === "message" ? (
                <WhatsappMessageItem
                  message={item.value}
                  messages={loadedMessages}
                  conversationId={conversationId}
                  onReplyPreviewClick={handleReplyPreviewClick}
                />
              ) : (
                <ChatDateHeader date={item.value} />
              )}
            </div>
          ))}
        </VList>
        {!debouncedAtBottom && (
          <div
            className="absolute right-[30px] bottom-[20px] z-50 flex h-[36px] w-[36px] cursor-pointer items-center justify-center rounded-[18px] bg-black"
            onClick={handleScrollToBottom}
          >
            <ChevronsDownIcon />
          </div>
        )}
      </MultiSectionLayout>
    </>
  );
});
